// ===========================================
// Portal Newsletters Page
// ===========================================
// Parent view for reading newsletters

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import {
  Box,
  Paper,
  TextField,
  Alert,
  Typography,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Grid,
  Stack,
  Skeleton,
  InputAdornment,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import { usePortalNewsletters } from '../../hooks/useNewsletter';

// ===========================================
// NEWSLETTER CARD
// ===========================================

interface NewsletterCardProps {
  id: string;
  title: string;
  excerpt: string | null;
  featuredImage: string | null;
  publishedAt: string | null;
  author: {
    firstName: string;
    lastName: string;
  };
  onClick: () => void;
}

function NewsletterCard({
  title,
  excerpt,
  featuredImage,
  publishedAt,
  author,
  onClick,
}: NewsletterCardProps) {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardActionArea onClick={onClick} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
        {featuredImage && (
          <CardMedia
            component="img"
            height="160"
            image={featuredImage}
            alt={title}
            sx={{ objectFit: 'cover' }}
          />
        )}
        <CardContent sx={{ flex: 1 }}>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          {excerpt && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {excerpt}
            </Typography>
          )}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="caption" color="text.secondary">
              By {author.firstName} {author.lastName}
            </Typography>
            {publishedAt && (
              <>
                <Typography variant="caption" color="text.secondary">
                  •
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(publishedAt).toLocaleDateString()}
                </Typography>
              </>
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

// ===========================================
// LOADING SKELETON
// ===========================================

function NewsletterCardSkeleton() {
  return (
    <Card>
      <Skeleton variant="rectangular" height={160} />
      <CardContent>
        <Skeleton variant="text" sx={{ fontSize: '1.5rem' }} />
        <Skeleton variant="text" />
        <Skeleton variant="text" width="60%" />
      </CardContent>
    </Card>
  );
}

// ===========================================
// COMPONENT
// ===========================================

export default function NewslettersPortalPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const { data: newslettersData, isLoading, error } = usePortalNewsletters({
    search: searchTerm || undefined,
    sortBy: 'publishedAt',
    sortOrder: 'desc',
  });

  const newsletters = newslettersData?.data || [];

  // Mounted under `/:schoolSlug/parent/newsletters`; the old bare `/portal/…`
  // path was never routed, so every card click 404'd (2026-09-09).
  const { schoolSlug } = useTenant();
  const handleNewsletterClick = (slug: string) => {
    navigate(`/${schoolSlug}/parent/newsletters/${slug}`);
  };

  return (
    <Box>
      <PageHeader
        title="Newsletters"
        subtitle="Stay updated with the latest news from our music school"
      />

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search newsletters..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          size="small"
        />
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load newsletters
        </Alert>
      )}

      {/* Newsletter grid */}
      {isLoading ? (
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <NewsletterCardSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : newsletters.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {searchTerm
              ? 'No newsletters found matching your search.'
              : 'No newsletters available yet.'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {newsletters.map((newsletter) => (
            <Grid item xs={12} sm={6} md={4} key={newsletter.id}>
              <NewsletterCard
                id={newsletter.id}
                title={newsletter.title}
                excerpt={newsletter.excerpt}
                featuredImage={newsletter.featuredImage}
                publishedAt={newsletter.publishedAt}
                author={newsletter.author}
                onClick={() => handleNewsletterClick(newsletter.slug)}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
